import React, { useState } from 'react';
import {
 CheckCircle2,
 AlertCircle,
 CheckCircle,
 XCircle,
 Clock,
 Calendar,
 Plus,
 Edit,
 Trash2,
 Eye,
 Loader2,
 ListChecks,
} from 'lucide-react';
import { Button, SearchSelect } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import DatePicker from '../../components/client/DatePicker';
import { useAdminCompliance } from '../../hooks/useAdminCompliance';
import { formatDate, formatTime } from '../../utils/dateUtils';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';

const AdminCompliance = () => {
 const [showCreateModal, setShowCreateModal] = useState(false);
 const [editingRecord, setEditingRecord] = useState(null);
 const {
 records,
 isLoading,
 error,
 filters,
 statusOptions,
 handleFilterChange,
 handleSearch,
 statistics: stats,
 getStatusColor
 } = useAdminCompliance();

 const columns = [
 {
 key: 'id',
 label: 'Record ID',
 sortable: true,
 width: 'w-32',
 render: (record) => (
 <span className="font-mono text-sm font-medium text-ink">
 #{record.id.substring(0, 8)}
 </span>
 )
 },
 {
 key: 'compliance_type',
 label: 'Type',
 sortable: true,
 width: 'w-40',
 render: (record) => (
 <span className="text-sm font-medium text-ink capitalize">
 {record.compliance_type?.replace('_', ' ') || 'N/A'}
 </span>
 )
 },
 {
 key: 'title',
 label: 'Title',
 sortable: true,
 width: 'w-64',
 render: (record) => (
 <p className="text-sm font-medium text-ink truncate">
 {record.title || 'N/A'}
 </p>
 )
 },
 {
 key: 'description',
 label: 'Description',
 sortable: false,
 width: 'w-96',
 render: (record) => (
 <p className="text-sm text-ink truncate">
 {record.description || 'N/A'}
 </p>
 )
 },
 {
 key: 'status',
 label: 'Status',
 sortable: true,
 width: 'w-32',
 render: (record) => (
 <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(record.status)}`}>
 {record.status ? record.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'N/A'}
 </span>
 )
 },
 {
 key: 'due_date',
 label: 'Due Date',
 sortable: true,
 width: 'w-32',
 render: (record) => (
 record.due_date ? (
 <div className="flex items-center space-x-2">
 <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
 <div className="min-w-0">
 <div className="text-sm font-medium text-ink">
 {formatDate(record.due_date, 'short')}
 </div>
 </div>
 </div>
 ) : (
 <span className="text-sm text-gray-400">N/A</span>
 )
 )
 },
 {
 key: 'assigned_to',
 label: 'Assigned To',
 sortable: true,
 width: 'w-48',
 render: (record) => (
 record.assigned_to ? (
 <div className="min-w-0">
 <p className="text-sm font-medium text-ink truncate">
 {record.assignedTo?.name || 'N/A'}
 </p>
 <p className="text-xs text-ink-tertiary truncate">
 {record.assignedTo?.email || ''}
 </p>
 </div>
 ) : (
 <span className="text-sm text-gray-400">Unassigned</span>
 )
 )
 },
 {
 key: 'created_at',
 label: 'Created',
 sortable: true,
 width: 'w-32',
 render: (record) => (
 <div className="flex items-center space-x-2">
 <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
 <div className="min-w-0">
 <div className="text-sm font-medium text-ink">
 {formatDate(record.created_at, 'medium')}
 </div>
 <div className="text-xs text-ink-tertiary">
 {formatTime(record.created_at)}
 </div>
 </div>
 </div>
 )
 }
 ];

 const actions = [
 {
 label: 'View',
 icon: Eye,
 variant: 'default',
 onClick: (record) => {
 console.log('View record:', record);
 }
 },
 {
 label: 'Edit',
 icon: Edit,
 variant: 'default',
 onClick: (record) => {
 setEditingRecord(record);
 setShowCreateModal(true);
 }
 },
 {
 label: 'Delete',
 icon: Trash2,
 variant: 'danger',
 onClick: (record) => {
 if (window.confirm('Are you sure you want to delete this compliance record?')) {
 // deleteRecord(record.id);
 }
 }
 }
 ];

 if (error) {
 return (
 <div className="space-y-5">
 <AdminPageHeader
 title="Compliance"
 subtitle="Manage compliance records and track regulatory requirements"
 />
 <div className="rounded-lg border border-red-200 bg-surface p-6 text-center shadow-sm">
 <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
 <h3 className="text-xl font-bold text-ink mb-2">Error Loading Compliance Records</h3>
 <p className="text-ink-secondary">
 {error?.response?.data?.message || 'Failed to load compliance records. Please try again later.'}
 </p>
 </div>
 </div>
 );
 }

 return (
 <div className="space-y-5">
 <AdminPageHeader
 title="Compliance"
 subtitle="Manage compliance records and track regulatory requirements"
 actions={
 <Button
 className="rounded-md flex items-center gap-2"
 onClick={() => {
 setEditingRecord(null);
 setShowCreateModal(true);
 }}
 >
 <Plus className="w-4 h-4" />
 Create Record
 </Button>
 }
 />

 <AdminGuideCallout
 title={ADMIN_PAGE_GUIDES.compliance.title}
 steps={ADMIN_PAGE_GUIDES.compliance.steps}
 icon={ListChecks}
 />

 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
 <div className="product-panel p-4 sm:p-5">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-ink-secondary">Total Records</p>
 <p className="text-2xl font-bold text-ink mt-1 tabular-nums">{stats.total_records || 0}</p>
 </div>
 <CheckCircle2 className="w-8 h-8 text-gray-400" />
 </div>
 </div>
 <div className="product-panel p-4 sm:p-5">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-ink-secondary">Pending</p>
 <p className="text-2xl font-bold text-amber-600 mt-1 tabular-nums">{stats.pending || 0}</p>
 </div>
 <Clock className="w-8 h-8 text-amber-500" />
 </div>
 </div>
 <div className="product-panel p-4 sm:p-5">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-ink-secondary">Completed</p>
 <p className="text-2xl font-bold text-emerald-600 mt-1 tabular-nums">{stats.completed || 0}</p>
 </div>
 <CheckCircle className="w-8 h-8 text-emerald-500" />
 </div>
 </div>
 <div className="product-panel p-4 sm:p-5">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-ink-secondary">Overdue</p>
 <p className="text-2xl font-bold text-red-600 mt-1 tabular-nums">{stats.overdue || 0}</p>
 </div>
 <AlertCircle className="w-8 h-8 text-red-500" />
 </div>
 </div>
 </div>

 <div className="product-panel overflow-hidden">
 <div className="p-4 sm:p-5 space-y-5">
 <div className="rounded-xl border border-border bg-surface-muted p-4">
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
 <SearchSelect
 label="Status"
 value={filters.status}
 onChange={(value) => handleFilterChange('status', value)}
 options={statusOptions}
 />
 <DatePicker
 label="From Date"
 value={filters.date_from}
 onChange={(value) => handleFilterChange('date_from', value)}
 />
 <DatePicker
 label="To Date"
 value={filters.date_to}
 onChange={(value) => handleFilterChange('date_to', value)}
 />
 </div>
 </div>

 {isLoading ? (
 <div className="py-20 text-center">
 <Loader2 className="w-8 h-8 animate-spin text-ink mx-auto mb-3" />
 <p className="text-sm text-ink-tertiary">Loading compliance records…</p>
 </div>
 ) : (
 <DataTable
 data={records}
 columns={columns}
 actions={actions}
 isLoading={false}
 searchPlaceholder="Search by title, description, or compliance type..."
 onSearch={handleSearch}
 emptyMessage="No compliance records found"
 className="!border-0 !shadow-none"
 />
 )}
 </div>
 </div>
 </div>
 );
};

export default AdminCompliance;
